/* eslint-disable no-continue */
import { getTranslatedSections } from "./section-items";
import { LanguageKey } from "../../../types";
import {
  EditableSection,
  GqlField,
  GqlFieldValues,
  GqlGroup,
  GqlGroupValues,
  GqlSectionValues,
} from "../types";

export type ChangeStatus = "added" | "edited" | "removed" | "moved";

export type EntityChangesMap = Map<string, ChangeStatus>;
export type SectionChangeCounts = Map<string, number>;

export type SectionChangeEntry = {
  uri: string;
  title: string;
  count: number;
  removed: boolean;
};

export const isChanged = (
  status: ChangeStatus | undefined,
): status is "added" | "edited" | "moved" =>
  status === "added" || status === "edited" || status === "moved";

export const changeStatusLabel: Record<
  Exclude<ChangeStatus, "removed">,
  string
> = {
  added: "Added",
  edited: "Edited",
  moved: "Moved",
};

// Keys that are excluded from content comparison
const EXCLUDED_KEYS = new Set(["order", "updatedAt", "operationId"]);

const stableStringify = (v: unknown): string =>
  JSON.stringify(v, (_, val) =>
    val && typeof val === "object" && !Array.isArray(val)
      ? Object.keys(val)
          .sort()
          .reduce<Record<string, unknown>>((sorted, k) => {
            sorted[k] = val[k];
            return sorted;
          }, {})
      : val,
  );

const valuesEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (typeof a !== "object" || typeof b !== "object") return false;
  return stableStringify(a) === stableStringify(b);
};

const hasContentChanged = <T extends Record<string, unknown>>(
  current: T,
  published: T,
): boolean => {
  const keys = [
    ...new Set([...Object.keys(current), ...Object.keys(published)]),
  ];
  return keys.some((key) => {
    if (EXCLUDED_KEYS.has(key)) return false;
    return !valuesEqual(current[key], published[key]);
  });
};

const hasOrderChanged = <T extends { order?: string | null }>(
  current: T,
  published: T,
): boolean => current.order !== published.order;

const computeEntityChanges = <
  T extends { uri: string; updatedAt: string; order?: string | null },
>(
  currentItems: T[],
  publishedItems: T[],
): Map<string, ChangeStatus> => {
  const map = new Map<string, ChangeStatus>();
  const publishedByUri = new Map(
    publishedItems.map((item) => [item.uri, item]),
  );
  const currentByUri = new Set(currentItems.map((item) => item.uri));

  for (const item of currentItems) {
    const published = publishedByUri.get(item.uri);

    if (!published) {
      map.set(item.uri, "added");
    } else if (item.updatedAt > published.updatedAt) {
      if (hasContentChanged(item, published)) {
        map.set(item.uri, "edited");
      } else if (hasOrderChanged(item, published)) {
        map.set(item.uri, "moved");
      }
    }
  }

  for (const item of publishedItems) {
    if (!currentByUri.has(item.uri)) {
      map.set(item.uri, "removed");
    }
  }

  return map;
};

export const computeFieldChanges = (
  currentFields: GqlFieldValues[],
  publishedFields: GqlFieldValues[],
): EntityChangesMap => computeEntityChanges(currentFields, publishedFields);

export const computeSectionChanges = (
  currentSections: GqlSectionValues[],
  publishedSections: GqlSectionValues[],
): EntityChangesMap => computeEntityChanges(currentSections, publishedSections);

export const computeGroupChanges = (
  currentGroups: GqlGroupValues[],
  publishedGroups: GqlGroupValues[],
): EntityChangesMap => computeEntityChanges(currentGroups, publishedGroups);

type ComputeSectionChangeCountsInput = {
  fieldChangesMap: EntityChangesMap;
  sectionChangesMap: EntityChangesMap;
  groupChangesMap: EntityChangesMap;
  fieldUriToSectionUri: Map<string, string>;
  groupUriToSectionUri: Map<string, string>;
};

export const computeSectionChangeCounts = ({
  fieldChangesMap,
  sectionChangesMap,
  groupChangesMap,
  fieldUriToSectionUri,
  groupUriToSectionUri,
}: ComputeSectionChangeCountsInput): SectionChangeCounts => {
  const counts = new Map<string, number>();

  const increment = (sectionUri: string) => {
    counts.set(sectionUri, (counts.get(sectionUri) ?? 0) + 1);
  };

  for (const [sectionUri] of sectionChangesMap) {
    increment(sectionUri);
  }

  for (const [groupUri] of groupChangesMap) {
    const sectionUri = groupUriToSectionUri.get(groupUri);
    if (sectionUri) increment(sectionUri);
  }

  for (const [fieldUri] of fieldChangesMap) {
    const sectionUri = fieldUriToSectionUri.get(fieldUri);
    if (sectionUri) increment(sectionUri);
  }

  return counts;
};

export const buildSectionChangeEntries = ({
  sectionChangeCounts,
  sectionChangesMap,
  currentSections,
  publishedSections = [],
  viewingLanguage,
}: {
  sectionChangeCounts: SectionChangeCounts;
  sectionChangesMap: EntityChangesMap;
  currentSections: EditableSection[];
  publishedSections?: EditableSection[];
  viewingLanguage: LanguageKey;
}): SectionChangeEntry[] => {
  const currentSectionUris = new Set(currentSections.map((s) => s.uri));
  const translatedCurrent = getTranslatedSections(
    currentSections,
    viewingLanguage,
  );
  const publishedTitleByUri = new Map(
    getTranslatedSections(publishedSections, viewingLanguage).map((section) => [
      section.uri,
      section.values?.title || "Section",
    ]),
  );

  const entries: SectionChangeEntry[] = [];

  for (const section of translatedCurrent) {
    const count = sectionChangeCounts.get(section.uri) ?? 0;
    if (count > 0) {
      entries.push({
        uri: section.uri,
        title: section.values?.title || "Section",
        count,
        removed: false,
      });
    }
  }

  for (const [sectionUri, status] of sectionChangesMap) {
    if (status !== "removed" || currentSectionUris.has(sectionUri)) continue;
    entries.push({
      uri: sectionUri,
      title: publishedTitleByUri.get(sectionUri) ?? "Section",
      count: sectionChangeCounts.get(sectionUri) ?? 1,
      removed: true,
    });
  }

  return entries;
};

export const buildEntitiesLookups = ({
  fields,
  publishedFields,
  groups,
  publishedGroups,
}: {
  fields?: GqlField[] | null;
  publishedFields?: GqlField[] | null;
  groups?: GqlGroup[] | null;
  publishedGroups?: GqlGroup[] | null;
}) => {
  const fieldUriToSectionUri = new Map<string, string>();
  const fieldUriToGroupUri = new Map<string, string | undefined>();
  const groupUriToSectionUri = new Map<string, string>();

  for (const field of [
    ...(fields ?? []).map((f) => f.values),
    ...(publishedFields ?? []).map((f) => f.values),
  ]) {
    if (!fieldUriToSectionUri.has(field.uri)) {
      fieldUriToSectionUri.set(field.uri, field.sectionUri);
    }
    if (!fieldUriToGroupUri.has(field.uri)) {
      fieldUriToGroupUri.set(field.uri, field.groupUri ?? undefined);
    }
  }

  for (const group of [
    ...(groups ?? []).map((g) => g.values),
    ...(publishedGroups ?? []).map((g) => g.values),
  ]) {
    if (!groupUriToSectionUri.has(group.uri)) {
      groupUriToSectionUri.set(group.uri, group.sectionUri);
    }
  }

  return {
    fieldUriToSectionUri,
    fieldUriToGroupUri,
    groupUriToSectionUri,
  };
};
